import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, ChevronLeft, ChevronRight, FilterX, Download, Upload, Plus, Archive, Pencil, X, Trash2, Search } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import { apiFetch } from "../api/apiFetch";
import { useWarehouseScope } from "../access/useWarehouseScope";
import { downloadXlsx } from "./helpers/exportFormats";
import { readXlsxRows } from "./helpers/xlsxRead";
import "../styles/bundles-admin.css";

// Admin › Warehouse › Bundles (plan: feat-bundles.md, Alex 2026-10-24).
//
// A bundle is ONE channel SKU that stands for N units of one or more
// Portal SKUs ("10 × LPN-ABC"). The definition lives only in the bundles
// tables — never in products or inventory — and order ingest rewrites a
// bundle line into its component lines. This page is the table of those
// definitions with the live "complete bundles" figure (min over components
// of floor(available / qty)), a create / edit modal, a spreadsheet upload
// (one row per component) and the matching export.

const PAGE_SIZE = 25;
// BE caps page_size at 200 — the export pages through in these chunks.
const EXPORT_CHUNK = 200;
// BE caps a bulk upload at 5000 rows. Rows of one bundle must travel in
// the same request (a bundle in the file REPLACES its component set), so
// the upload is one request rather than chunked.
const UPLOAD_MAX_ROWS = 5000;

export interface BundleComponent {
    sku: string;
    quantity: number;
    title: string | null;
    barcode: string | null;
    image_url: string | null;
    archived: boolean;
    available: number;
}

export interface BundleRow {
    id: number;
    bundle_sku: string;
    title: string | null;
    sync_jobalots: boolean;
    sync_bargainfox: boolean;
    created_by_name: string | null;
    created_at: string;
    updated_at: string;
    archived_at: string | null;
    components: BundleComponent[];
    complete_bundles: number;
}

// ---- Upload column mapping ------------------------------------------------
// One sheet row per component. Headers are matched on a relaxed form
// (lower-case, letters and digits only). Title and the sync flags are
// read from the bundle's first row that has them.
export const UPLOAD_COLUMNS = ["Bundle SKU", "Title", "Component SKU", "Quantity", "Sync Jobalots", "Sync BargainFox"] as const;
export type UploadField = "bundle_sku" | "title" | "component_sku" | "quantity" | "sync_jobalots" | "sync_bargainfox";
const FIELD_ORDER: UploadField[] = ["bundle_sku", "title", "component_sku", "quantity", "sync_jobalots", "sync_bargainfox"];
export const REQUIRED_FIELDS: UploadField[] = ["bundle_sku", "component_sku", "quantity"];
const HEADER_ALIASES: Record<string, UploadField> = {
    bundlesku: "bundle_sku", bundle: "bundle_sku", sku: "bundle_sku", kitsku: "bundle_sku", channelsku: "bundle_sku", listingsku: "bundle_sku",
    title: "title", bundletitle: "title", name: "title", description: "title",
    componentsku: "component_sku", component: "component_sku", portalsku: "component_sku", childsku: "component_sku", itemsku: "component_sku",
    quantity: "quantity", qty: "quantity", units: "quantity", componentquantity: "quantity",
    syncjobalots: "sync_jobalots", jobalots: "sync_jobalots",
    syncbargainfox: "sync_bargainfox", bargainfox: "sync_bargainfox",
};
const relaxHeader = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, "");

export const mapUploadHeader = (header: string[]): { fields: (UploadField | null)[]; missing: UploadField[] } => {
    const fields = header.map(h => HEADER_ALIASES[relaxHeader(h)] ?? null);
    const present = new Set(fields.filter((f): f is UploadField => f !== null));
    return { fields, missing: REQUIRED_FIELDS.filter(f => !present.has(f)) };
};

// Sheet rows → the `{ rows }` body the BE expects. Blank cells are omitted
// so the BE treats them as "not given" (a blank sync cell leaves the
// existing switch alone on an update).
export const rowsFromSheet = (fields: (UploadField | null)[], rows: string[][]): Record<string, string>[] =>
    rows.map(cells => {
        const out: Record<string, string> = {};
        fields.forEach((f, i) => {
            if (!f) return;
            const v = (cells[i] ?? "").trim();
            if (v !== "") out[f] = v;
        });
        return out;
    });

// Table / export: one row per component, same columns as the template, so
// a Download → edit → Upload round-trip is lossless.
export const exportRows = (bundles: BundleRow[]): (string | number)[][] =>
    bundles.flatMap(b => b.components.map(c => [
        b.bundle_sku, b.title ?? "", c.sku, c.quantity, b.sync_jobalots ? "yes" : "no", b.sync_bargainfox ? "yes" : "no",
    ]));

const formatDate = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const cell = (v: string | number | null | undefined) => (v === null || v === undefined || v === "" ? "—" : String(v));

// ---- Create / edit modal ---------------------------------------------------

interface DraftComponent {
    key: number;
    sku: string;
    quantity: string;
    // Product lookup result: undefined = not looked up, null = not found.
    found?: { title: string | null } | null;
    lookupFailed?: boolean;
}

let draftKey = 0;
const newDraftComponent = (sku = "", quantity = "1"): DraftComponent => ({ key: ++draftKey, sku, quantity });

interface BundleModalProps {
    bundle: BundleRow | null;    // null = create
    onClose: () => void;
    onSaved: () => void;
}

const BundleModal = ({ bundle, onClose, onSaved }: BundleModalProps) => {
    const { t } = useTranslation();
    const isEdit = bundle !== null;
    const [sku, setSku] = useState(bundle?.bundle_sku ?? "");
    const [title, setTitle] = useState(bundle?.title ?? "");
    const [syncJobalots, setSyncJobalots] = useState(bundle?.sync_jobalots ?? false);
    const [syncBargainfox, setSyncBargainfox] = useState(bundle?.sync_bargainfox ?? false);
    const [components, setComponents] = useState<DraftComponent[]>(
        bundle && bundle.components.length > 0
            ? bundle.components.map(c => ({ ...newDraftComponent(c.sku, String(c.quantity)), found: { title: c.title } }))
            : [newDraftComponent()],
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const updateComponent = (key: number, patch: Partial<DraftComponent>) =>
        setComponents(list => list.map(c => (c.key === key ? { ...c, ...patch } : c)));

    // Look the SKU up so the admin sees the product title before saving.
    // Purely informational — the BE validates again on save.
    const lookup = async (c: DraftComponent) => {
        const q = c.sku.trim();
        if (!q) return;
        try {
            const res = await apiFetch(`/products/exact-search?field=sku&q=${encodeURIComponent(q)}`);
            if (!res.ok) { updateComponent(c.key, { lookupFailed: true, found: undefined }); return; }
            const data = await res.json();
            const hit = Array.isArray(data.products) ? data.products[0] : null;
            updateComponent(c.key, { lookupFailed: false, found: hit ? { title: hit.title ?? null } : null });
        } catch {
            updateComponent(c.key, { lookupFailed: true, found: undefined });
        }
    };

    const save = async () => {
        setError(null);
        const bundleSku = sku.trim();
        if (!isEdit && !bundleSku) { setError(t("bundles_field_sku_required")); return; }
        const shaped = components
            .map(c => ({ sku: c.sku.trim(), quantity: Number(c.quantity) }))
            .filter(c => c.sku !== "");
        if (shaped.length === 0) { setError(t("bundles_components_required")); return; }
        setSaving(true);
        try {
            const body = { bundle_sku: bundleSku, title: title.trim() || null, sync_jobalots: syncJobalots, sync_bargainfox: syncBargainfox, components: shaped };
            const res = await apiFetch(isEdit ? `/bundles/${bundle.id}` : "/bundles", {
                method: isEdit ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok) { setError(data?.message || t("server_error")); return; }
            toast.success(t("bundles_saved"), { toastId: "bundles-saved" });
            onSaved();
        } catch {
            setError(t("server_error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bundles-overlay" role="dialog" aria-modal="true" aria-label={isEdit ? t("bundles_modal_edit") : t("bundles_modal_new")}>
            <div className="bundles-modal">
                <div className="bundles-modal-head">
                    <h3>{isEdit ? t("bundles_modal_edit") : t("bundles_modal_new")}</h3>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label={t("close")}><X size={16} /></button>
                </div>
                <div className="bundles-modal-body">
                    <div className="bundles-field-row">
                        <div className="bundles-field">
                            <label htmlFor="bundle-sku">{t("bundles_field_sku")}</label>
                            <input id="bundle-sku" type="text" value={sku} maxLength={200} disabled={isEdit} autoFocus={!isEdit}
                                onChange={e => setSku(e.target.value)} placeholder="BUNDLE-ABC-10" />
                            {isEdit && <small style={{ color: "var(--color-text-muted)" }}>{t("bundles_sku_immutable_hint")}</small>}
                        </div>
                        <div className="bundles-field">
                            <label htmlFor="bundle-title">{t("bundles_field_title")}</label>
                            <input id="bundle-title" type="text" value={title} maxLength={1000} onChange={e => setTitle(e.target.value)} />
                        </div>
                    </div>
                    <div className="bundles-checks">
                        <label><input type="checkbox" checked={syncJobalots} onChange={e => setSyncJobalots(e.target.checked)} /> {t("bundles_sync_jobalots")}</label>
                        <label><input type="checkbox" checked={syncBargainfox} onChange={e => setSyncBargainfox(e.target.checked)} /> {t("bundles_sync_bargainfox")}</label>
                    </div>

                    <div className="bundles-field">
                        <label>{t("bundles_components_title")}</label>
                        <table className="bundles-comp-table">
                            <thead>
                                <tr>
                                    <th>{t("bundles_comp_sku")}</th>
                                    <th>{t("bundles_comp_qty")}</th>
                                    <th>{t("bundles_comp_product")}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {components.map(c => (
                                    <tr key={c.key}>
                                        <td>
                                            <input type="text" value={c.sku} maxLength={200} aria-label={t("bundles_comp_sku")}
                                                onChange={e => updateComponent(c.key, { sku: e.target.value, found: undefined, lookupFailed: false })}
                                                onBlur={() => lookup(c)}
                                                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); lookup(c); } }} />
                                        </td>
                                        <td>
                                            <input type="number" min={1} step={1} value={c.quantity} aria-label={t("bundles_comp_qty")}
                                                onChange={e => updateComponent(c.key, { quantity: e.target.value })} />
                                        </td>
                                        <td>
                                            {c.found === null && <span className="bundles-comp-found is-missing">{t("bundles_lookup_not_found")}</span>}
                                            {c.found && <span className="bundles-comp-found" title={c.found.title ?? undefined}>{c.found.title || "—"}</span>}
                                            {c.lookupFailed && <span className="bundles-comp-found">{t("bundles_lookup_unavailable")}</span>}
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => lookup(c)} aria-label={t("bundles_lookup")} title={t("bundles_lookup")}><Search size={14} /></button>
                                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setComponents(list => list.length > 1 ? list.filter(x => x.key !== c.key) : [newDraftComponent()])} aria-label={t("bundles_remove_component")} title={t("bundles_remove_component")}><Trash2 size={14} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setComponents(list => [...list, newDraftComponent()])}>
                                <Plus size={14} /> {t("bundles_add_component")}
                            </button>
                        </div>
                    </div>
                    {error && <p className="bundles-modal-error" role="alert">{error}</p>}
                </div>
                <div className="bundles-modal-foot">
                    <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>{t("close")}</button>
                    <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t("submitting") : t("bundles_save")}</button>
                </div>
            </div>
        </div>
    );
};

// ---- The page ------------------------------------------------------------------

const BundlesAdmin = () => {
    const { t } = useTranslation();

    const [rows, setRows] = useState<BundleRow[]>([]);
    const [total, setTotal] = useState(0);
    const [searchFilter, setSearchFilter] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState(searchFilter);
    useEffect(() => { setSearchInput(searchFilter); }, [searchFilter]);
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState("");
    // Without cross_warehouse the stock figure is pinned to the person's own warehouse.
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [showArchived, setShowArchived] = useState(false);
    const [allWarehouses, setWarehouses] = useState<string[]>([]);
    const warehouses = warehouseScope.limit(allWarehouses);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [modal, setModal] = useState<{ open: boolean; bundle: BundleRow | null }>({ open: false, bundle: null });
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => { setPage(0); }, [searchFilter, warehouseFilter, showArchived]);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: { warehouse: string }[] = await res.json();
                setWarehouses(data.map(r => r.warehouse).filter(Boolean).sort());
            } catch { /* leave empty */ }
        })();
    }, []);

    const buildFilterParams = useCallback(() => {
        const params = new URLSearchParams();
        if (searchFilter.trim()) params.set("q", searchFilter.trim());
        if (warehouseFilter) params.set("warehouse", warehouseFilter);
        if (showArchived) params.set("include_archived", "true");
        return params;
    }, [searchFilter, warehouseFilter, showArchived]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildFilterParams();
            params.set("page", String(page));
            params.set("page_size", String(PAGE_SIZE));
            const res = await apiFetch(`/bundles/list?${params}`, {});
            if (!res.ok) throw new Error(String(res.status));
            const data = await res.json();
            setRows(Array.isArray(data.bundles) ? data.bundles : []);
            setTotal(typeof data.total === "number" ? data.total : 0);
        } catch {
            toast.error(t("server_error"), { toastId: "bundles-load-err" });
        } finally {
            setLoading(false);
        }
    }, [buildFilterParams, page, t]);
    useEffect(() => { refresh(); }, [refresh]);

    const hasActiveFilters = Boolean(searchFilter.trim() || pickedWarehouse || showArchived);
    const clearFilters = () => { setSearchFilter(""); setSearchInput(""); setWarehouseFilter(""); setShowArchived(false); };

    const archiveBundle = async (b: BundleRow) => {
        if (!window.confirm(t("bundles_archive_confirm", { sku: b.bundle_sku }))) return;
        try {
            const res = await apiFetch(`/bundles/${b.id}`, { method: "DELETE" });
            if (!res.ok) throw new Error(String(res.status));
            toast.success(t("bundles_archived_done"), { toastId: "bundles-archived" });
            refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "bundles-archive-err" });
        }
    };

    // Collect EVERY bundle matching the current filters, paging through the BE cap.
    const fetchAllFiltered = async (): Promise<BundleRow[]> => {
        const all: BundleRow[] = [];
        let exportPage = 0;
        let expected = Infinity;
        do {
            const params = buildFilterParams();
            params.set("page", String(exportPage));
            params.set("page_size", String(EXPORT_CHUNK));
            const res = await apiFetch(`/bundles/list?${params}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const batch: BundleRow[] = Array.isArray(data.bundles) ? data.bundles : [];
            all.push(...batch);
            expected = typeof data.total === "number" ? data.total : all.length;
            exportPage += 1;
            if (batch.length === 0) break;
        } while (all.length < expected);
        return all;
    };

    const downloadTemplate = async () => {
        await downloadXlsx("bundles-template.xlsx", [...UPLOAD_COLUMNS], [], new Set([0, 2]), "Bundles");
    };

    const exportTable = async () => {
        setDownloading(true);
        try {
            const all = await fetchAllFiltered();
            await downloadXlsx("bundles.xlsx", [...UPLOAD_COLUMNS], exportRows(all), new Set([0, 2]), "Bundles");
        } catch {
            toast.error(t("server_error"), { toastId: "bundles-export-err" });
        } finally {
            setDownloading(false);
        }
    };

    const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploading(true);
        try {
            const { header, rows: sheetRows } = await readXlsxRows(file);
            const { fields, missing } = mapUploadHeader(header);
            if (missing.length > 0) {
                const names = missing.map(f => UPLOAD_COLUMNS[FIELD_ORDER.indexOf(f)]);
                toast.error(t("bundles_upload_missing_columns", { columns: names.join(", ") }), { toastId: "bundles-upload-cols" });
                return;
            }
            const shaped = rowsFromSheet(fields, sheetRows);
            if (shaped.length === 0) { toast.error(t("bundles_upload_empty"), { toastId: "bundles-upload-empty" }); return; }
            if (shaped.length > UPLOAD_MAX_ROWS) { toast.error(t("bundles_upload_too_many", { max: String(UPLOAD_MAX_ROWS) }), { toastId: "bundles-upload-max" }); return; }

            const res = await apiFetch("/bundles/bulk", {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rows: shaped }),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok) { toast.error(data?.message || t("bundles_upload_failed"), { toastId: "bundles-upload-err" }); return; }
            const errors: { bundle_sku: string | null; row: number; message: string }[] = Array.isArray(data?.errors) ? data.errors : [];
            toast.success(t("bundles_upload_done", { created: String(data?.created ?? 0), updated: String(data?.updated ?? 0) }), { toastId: "bundles-upload-done" });
            if (errors.length > 0) {
                const first = errors[0];
                toast.warn(t("bundles_upload_errors", {
                    count: String(errors.length),
                    sku: first.bundle_sku ?? "?",
                    // BE row numbers are data rows; the sheet header is row 1.
                    row: String(first.row + 1),
                    message: first.message,
                }), { toastId: "bundles-upload-rejected", autoClose: 15000 });
            }
            refresh();
        } catch {
            toast.error(t("bundles_upload_failed"), { toastId: "bundles-upload-err" });
        } finally {
            setUploading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const rangeFrom = total === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeTo = Math.min((safePage + 1) * PAGE_SIZE, total);

    return (
        <div className="inventory-admin">
            <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: "none" }} onChange={onFileChosen} data-testid="bundles-upload-input" />

            {modal.open && (
                <BundleModal
                    bundle={modal.bundle}
                    onClose={() => setModal({ open: false, bundle: null })}
                    onSaved={() => { setModal({ open: false, bundle: null }); refresh(); }}
                />
            )}

            <div className="totes-admin-actions">
                <button type="button" className="btn btn-primary" onClick={() => setModal({ open: true, bundle: null })}>
                    <Plus size={16} /> {t("bundles_new")}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    <Upload size={16} /> {uploading ? t("submitting") : t("bundles_upload")}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={downloadTemplate}>
                    <Download size={14} /> {t("bundles_template")}
                </button>
                <button type="button" className="btn btn-ghost" onClick={exportTable} disabled={loading || downloading || total === 0}>
                    <Download size={16} /> {downloading ? t("submitting") : t("bundles_export")}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters} disabled={!hasActiveFilters} style={{ marginLeft: "auto" }}>
                    <FilterX size={14} /> {t("wipe_clear_filters")}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={refresh} disabled={loading}>
                    <RefreshCw size={14} /> {t("refresh")}
                </button>
            </div>

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("bundles_filter_search_hint")}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); setSearchFilter(searchInput.trim()); } }}
                    onBlur={() => { if (searchInput.trim() !== searchFilter.trim()) setSearchFilter(searchInput.trim()); }}
                />
                <select
                    className="wh-admin-filter-select"
                    value={warehouseFilter}
                    onChange={e => setWarehouseFilter(e.target.value)}
                    disabled={!warehouseScope.canCross}
                    aria-label={t("bundles_col_complete")}
                >
                    {warehouseScope.canCross && <option value="">{t("bundles_filter_all_warehouses")}</option>}
                    {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
                <label className="bundles-archived-toggle">
                    <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> {t("bundles_show_archived")}
                </label>
            </div>

            <div className="locations-table-wrap">
                <table className="locations-table wipe-admin-table">
                    <thead>
                        <tr>
                            <th>{t("bundles_col_sku")}</th>
                            <th>{t("bundles_col_title")}</th>
                            <th>{t("bundles_col_components")}</th>
                            <th className="locations-table-num" title={t("bundles_complete_hint")}>{t("bundles_col_complete")}</th>
                            <th>{t("bundles_col_jobalots")}</th>
                            <th>{t("bundles_col_bargainfox")}</th>
                            <th>{t("bundles_col_updated")}</th>
                            <th>{t("bundles_col_by")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(b => (
                            <tr key={b.id} className={b.archived_at ? "is-archived" : ""}>
                                <td title={b.bundle_sku}>
                                    <strong>{b.bundle_sku}</strong>
                                    {b.archived_at && <span className="bundles-flag" style={{ marginLeft: 6 }}>{t("bundles_archived")}</span>}
                                </td>
                                <td title={b.title ?? undefined}>{cell(b.title)}</td>
                                <td>
                                    <ul className="bundles-components">
                                        {b.components.map(c => {
                                            const short = Math.floor(c.available / c.quantity) === b.complete_bundles && b.complete_bundles === 0;
                                            return (
                                                <li key={c.sku} className={`${c.archived ? "is-archived" : ""}${short ? " is-short" : ""}`}>
                                                    <span className="bundles-qty">{c.quantity} ×</span>
                                                    <span className="bundles-comp-sku">{c.sku}</span>
                                                    {c.title && <span className="bundles-comp-title" title={c.title}>{c.title}</span>}
                                                    <span className="bundles-comp-avail">{t("bundles_comp_available", { available: String(c.available) })}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </td>
                                <td className="locations-table-num">
                                    <span className={`bundles-complete${b.complete_bundles === 0 ? " is-zero" : ""}`}>{b.complete_bundles}</span>
                                </td>
                                <td><span className={`bundles-flag${b.sync_jobalots ? " is-on" : ""}`}>{b.sync_jobalots ? t("yes") : t("no")}</span></td>
                                <td><span className={`bundles-flag${b.sync_bargainfox ? " is-on" : ""}`}>{b.sync_bargainfox ? t("yes") : t("no")}</span></td>
                                <td>{formatDate(b.updated_at)}</td>
                                <td>{cell(b.created_by_name)}</td>
                                <td>
                                    {!b.archived_at && (
                                        <div className="bundles-row-actions">
                                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setModal({ open: true, bundle: b })}>
                                                <Pencil size={14} /> {t("bundles_edit")}
                                            </button>
                                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => archiveBundle(b)}>
                                                <Archive size={14} /> {t("bundles_archive")}
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {!loading && total === 0 && <div className="locations-table-empty">{t("bundles_no_rows")}</div>}

            {total > 0 && (
                <div className="locations-table-footer">
                    <span>{t("locations_showing_range", { from: String(rangeFrom), to: String(rangeTo), total: String(total) })}</span>
                    <div className="locations-table-pagination">
                        <button type="button" className="locations-table-page-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0} aria-label={t("pagination_previous")}>
                            <ChevronLeft size={16} />
                            <span>{t("pagination_previous")}</span>
                        </button>
                        <span className="locations-table-page-indicator">
                            {t("pagination_page_x_of_y", { page: String(safePage + 1), total: String(totalPages) })}
                        </span>
                        <button type="button" className="locations-table-page-btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} aria-label={t("pagination_next")}>
                            <span>{t("pagination_next")}</span>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BundlesAdmin;
